import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import Video from "twilio-video";
import MicOffIcon from "@icons/Rooms/MicOff.svg";
import { FontSize } from "@themes/Fonts";
import Styles from "@themes/Styles";
import * as RoomConstants from "../../RoomConstants";
import * as RoomTypes from "../../RoomTypes";
import { VideoChatContextType, useVideoChatContext } from "../../VideoChatContext";
import RoomScreenShareSelectorModal from "./RoomScreenShareSelectorModal";

const RoomParticipantView = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;

    min-width: 128px;
    min-height: 72px;
    height: 100%;
    width: 100%;
`;

const VideoWrapper = styled.div<{ enable: boolean }>`
    position: relative;
    height: inherit;
    width: inherit;
    background-color: #20252b;
    margin: auto;
    text-align: center;
    border: 1px solid darkgrey;
`;

const ParticipantVideo = styled.video<{ enable: boolean }>`
    visibility: ${(props) => (props.enable ? "visible" : "hidden")};
    align-self: center;

    height: inherit;
    width: inherit;

    -webkit-transform: scaleX(-1);
    transform: scaleX(-1);
`;

const ParticipantInfo = styled.div<{ camEnabled: boolean }>`
    position: absolute;

    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    height: auto;
    width: 100%;
    bottom: 0;

    border-radius: ${Styles.borderRadiusExtraSharp};
`;

const ParticipantInfoBackground = styled.span`
    position: absolute;
    z-index: 1;
    bottom: 0;

    height: 100%;
    width: 100%;

    background-color: black;
    opacity: 0.4;
`;

const ParticipantName = styled.span`
    z-index: 2;
    color: white;
    font-size: ${FontSize.normal};
`;

const MutedIcon = styled(MicOffIcon)`
    margin-left: 4px;
    fill: red;
    z-index: 2;
`;

const UserPlaceholderIconWrapper = styled.span`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
`;
const UserPlaceholderIcon = styled.img`
    height: 36px;
    width: 36px;
`;

const USER_VIDEO_PLACEHOLDER_EMOJI = "https://pub-aceabaa1ff6f48f7872c8684996a45ee.r2.dev/see-no-evil-monkey_1f648.png";
const SHARE_SCREEN_ERROR_MSG_SYSTEM_PERMISSIONS =
    "Could not share the screen. Please ensure that your browser has permissions to share screen by checking your device's system settings/preferences. You can try sharing a window instead, if you were trying to share your full screen.";

interface RoomParticipantProps {
    readonly participant: Video.Participant | Video.LocalParticipant;
    readonly participantDisplayName: string;
    /**
     * Host is 0, all other participants have number >= 1.
     */
    readonly participantNumber: number;
    readonly isLocalClient: boolean;
    readonly canShareScreen: boolean;
    readonly handleShareScreen: (share: boolean, track: any) => void;
    readonly localPart?: Video.LocalParticipant;
    readonly participantAviUrl?: string;
    readonly volume?: number;
}

/**
 * NOTE: Most of this implementation comes from these guides:
 *  Audio/Video:
 *      https://www.twilio.com/blog/video-chat-react-hooks
 *  Screen Share:
 *      https://www.twilio.com/blog/screen-sharing-javascript-twilio-programmable-video
 *      https://github.com/philnash/twilio-video-react-hooks/issues/21
 *
 * @param props
 * @constructor
 */
export default function RoomParticipant(props: RoomParticipantProps) {
    const {
        isMyCameraOn,
        selectedCameraDevice,
        isMyMicOn,
        selectedMicDevice,
        isMyScreenShared,
        setIsMyScreenShared,
    }: VideoChatContextType = useVideoChatContext();

    const [screenShareSources, setScreenShareSources] = useState<Electron.DesktopCapturerSource[] | undefined>(
        undefined,
    );
    const [selectedScreenShareSource, setSelectedScreenShareSource] = useState<
        Electron.DesktopCapturerSource | undefined
    >(undefined);

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    // Screen share ref is passed from parent component, since only 1 user in a video call
    // should share screen at one time.

    useEffect(() => {
        if (!props.isLocalClient && audioRef.current != null && props.volume) {
            audioRef.current.volume = props.volume;
        }
    }, [props.volume]);

    // Handle User Microphone Toggle
    useEffect(() => {
        if (props.isLocalClient && props.localPart) {
            const audio = [...props.localPart.audioTracks.values()][0];
            if (audio) {
                if (isMyMicOn) {
                    audio.track.restart().then(() => {
                        audio.track.enable();
                        setMicOn(true);
                    });
                } else {
                    audio.track.disable();
                    audio.track.stop();
                    setMicOn(false);
                }
            } else {
                alert("Could not get audio track.");
            }
        }
    }, [isMyMicOn]);

    // Handle User Webcam Toggle
    useEffect(() => {
        if (props.isLocalClient && props.localPart) {
            const vid = [...props.localPart.videoTracks.values()][0];
            if (vid) {
                if (isMyCameraOn) {
                    // Camera was stopped after being disabled, so we first restart then call enable().
                    vid.track.restart().then(() => {
                        vid.track.enable();
                        setCamOn(true);
                    });
                } else {
                    // Disable does not turn off the camera completely (active light still on)
                    // so we first call stop().
                    vid.track.disable();
                    vid.track.stop();
                    setCamOn(false);
                }
            } else {
                alert("Could not get video track.");
            }
        }
    }, [isMyCameraOn]);

    const handleStopScreenShare = useCallback(
        (track: any) => {
            props.handleShareScreen(false, track);
            setIsMyScreenShared(false);
        },
        [props.participant],
    );

    const shareScreenByStream = (stream: MediaStream) => {
        // Callback to fire when the screen video track is published.
        const handleSuccessfulTrackPublish = (screenTrack: Video.LocalVideoTrack) => {
            // Track was successfully published, so we attach it to the
            // video element for rendering.
            props.handleShareScreen(true, screenTrack);

            // Handle stopping screen share via the native browser controls.
            screenTrack.on("stopped", () => {
                handleStopScreenShare(screenTrack);
            });
        };

        // Get the track from the media stream and create new local track.
        const tracks = stream.getTracks();
        if (tracks[0]) {
            const screenTrack = new Video.LocalVideoTrack(tracks[0], {
                name: RoomConstants.VideoChatTrackNames.USER_SCREEN,
                logLevel: undefined, // logLevel is deprecated but removing it causes an error
            });

            // Then publish them so all users can see.
            props.localPart
                ?.publishTrack(screenTrack)
                .then(() => handleSuccessfulTrackPublish(screenTrack))
                .catch(() => {
                    // Don't need to call handleStopScreenShare() because track was not
                    //  published or attached to video element.
                    setIsMyScreenShared(false);
                    alert(SHARE_SCREEN_ERROR_MSG_SYSTEM_PERMISSIONS);
                });
        }
    };

    const onScreenShareError = () => {
        // Don't need to call handleStopScreenShare() because track was not published
        // or attached to video element.
        setIsMyScreenShared(false);
        alert(SHARE_SCREEN_ERROR_MSG_SYSTEM_PERMISSIONS);
    };

    // Handle User Claiming/Relinquishing Screen Share
    // TODO: handle different screen share errors with different messages
    useLayoutEffect(() => {
        // useLayoutEffect instead of useEffect to handle safari on gesture
        if (props.isLocalClient && props.localPart) {
            if (isMyScreenShared) {
                // Note: screen share track is still a video track
                if (window.todesktop) {
                    // https://docs.todesktop.com/recipes/capture-screen-contents
                    // Get available sources and set screenShareSources to display selector modal
                    window.todesktop.desktopCapturer
                        .getSources({
                            types: ["window", "screen"],
                            thumbnailSize: {
                                height: 720,
                                width: 1000,
                            },
                        })
                        .then((sources: Electron.DesktopCapturerSource[]) => {
                            setScreenShareSources(sources);
                        });
                } else {
                    navigator.mediaDevices
                        .getDisplayMedia()
                        .then((stream: MediaStream) => {
                            shareScreenByStream(stream);
                        })
                        .catch(() => {
                            onScreenShareError();
                        });
                }
            } else {
                // Disable the current user's screen sharing
                const screenTrackPub = [...props.localPart.videoTracks.values()].filter(filterScreenShareTrack)[0];

                if (screenTrackPub) {
                    const screenTrack = screenTrackPub.track;
                    screenTrack.stop();
                    handleStopScreenShare(screenTrack); // detaches from the <video/> tag
                    props.localPart.unpublishTrack(screenTrack);
                }
            }
        }
    }, [isMyScreenShared]);

    // For desktop app, once source is selected, start screen share
    useEffect(() => {
        if (selectedScreenShareSource) {
            // Note: see global.d.ts for typing
            (navigator.mediaDevices as any)
                .getUserMedia({
                    audio: false,
                    video: {
                        mandatory: {
                            chromeMediaSource: "desktop",
                            chromeMediaSourceId: selectedScreenShareSource.id,
                            minWidth: 1280,
                            maxWidth: 1280,
                            minHeight: 720,
                            maxHeight: 720,
                        },
                    },
                })
                .then((stream: MediaStream) => {
                    shareScreenByStream(stream);
                })
                .catch(() => {
                    setSelectedScreenShareSource(undefined);
                    onScreenShareError();
                });
        }
    }, [selectedScreenShareSource]);

    // State variables used for handling the rendered component(s)
    const [micOn, setMicOn] = useState([...props.participant.audioTracks.values()][0]?.isTrackEnabled);
    const [camOn, setCamOn] = useState([...props.participant.videoTracks.values()][0]?.isTrackEnabled);

    // This hook handles making tracks displayed/heard (via the JSX that we render)
    // when the tracks are enabled/disabled.
    useEffect(() => {
        // Handling for User Microphone Audio Tracks
        const audioTrackPub = getUserMicTrack([...props.participant.audioTracks.values()]);
        if (audioTrackPub) {
            audioTrackPub.on("trackEnabled", () => {
                setMicOn(true);
            });
            audioTrackPub.on("trackDisabled", () => {
                setMicOn(false);
            });
        }

        // Handling for User Webcam Video Track
        const videoTrackPub = getUserWebcamTrack([...props.participant.videoTracks.values()]);
        if (videoTrackPub) {
            videoTrackPub.on("trackEnabled", () => {
                setCamOn(true);
            });
            videoTrackPub.on("trackDisabled", () => {
                setCamOn(false);
            });
        }

        // Handling for User Screen Share Video Track
        const screenTrackPub = getUserScreenTrack([...props.participant.videoTracks.values()]);
        if (screenTrackPub) {
            screenTrackPub.on("trackEnabled", (track: any) => {
                props.handleShareScreen(true, track);
            });
            screenTrackPub.on("trackDisabled", (track: any) => {
                handleStopScreenShare(track);
            });
        }

        return () => {
            props.participant.removeAllListeners();
        };
    }, [props.participant]);

    const [audioTracks, setAudioTracks] = useState<RoomTypes.TrackType[]>([]);
    const [videoTracks, setVideoTracks] = useState<RoomTypes.TrackType[]>([]);
    const [screenTracks, setScreenTracks] = useState<RoomTypes.TrackType[]>([]); // TODO: since only 1 screen should be shared at a time, this shouldn't be an array

    // Handle track subscriptions for all possible track types (mic, webcam, screen share)
    useEffect(() => {
        const trackSubscribed = (track: RoomTypes.TrackType) => {
            if (track.kind === "video") {
                if (track.name === RoomConstants.VideoChatTrackNames.USER_SCREEN) {
                    const updatedTracks = [...screenTracks, track];
                    setScreenTracks(updatedTracks);
                } else {
                    const updatedTracks = [...videoTracks, track];
                    setVideoTracks(updatedTracks);
                }
            } else if (track.kind === "audio") {
                const updatedTracks = [...audioTracks, track];
                setAudioTracks(updatedTracks);
            }
        };

        const trackUnsubscribed = (track: RoomTypes.TrackType) => {
            if (track.kind === "video") {
                if (track.name === RoomConstants.VideoChatTrackNames.USER_SCREEN) {
                    const updatedTracks = screenTracks.filter((t) => t !== track);
                    setScreenTracks(updatedTracks);
                } else {
                    const updatedTracks = videoTracks.filter((v) => v !== track);
                    setVideoTracks(updatedTracks);
                }
            } else if (track.kind === "audio") {
                const updatedTracks = audioTracks.filter((v) => v !== track);
                setAudioTracks(updatedTracks);
            }
        };

        setAudioTracks(trackpubsToTracks(Array.from(props.participant.audioTracks.values())));

        setVideoTracks(trackpubsToTracks(Array.from(props.participant.videoTracks.values()).filter(filterWebcamTrack)));

        setScreenTracks(
            trackpubsToTracks(Array.from(props.participant.videoTracks.values()).filter(filterScreenShareTrack)),
        );

        props.participant.on("trackSubscribed", trackSubscribed);
        props.participant.on("trackUnsubscribed", trackUnsubscribed);

        return () => {
            setAudioTracks([]);
            setVideoTracks([]);
            setScreenTracks([]);
            props.participant.removeAllListeners();
        };
    }, [props.participant]);

    // Handle attaching/detaching user audio tracks
    useEffect(() => {
        const audioTrack = audioTracks[0];
        if (audioTrack) {
            audioTrack.attach(audioRef.current ?? undefined);
            return () => {
                audioTrack.detach();
            };
        }
        return undefined;
    }, [audioTracks]);

    useEffect(() => {
        const audioTrack = audioTracks[0];
        if (audioTrack && props.localPart && selectedMicDevice) {
            props.localPart?.audioTracks.forEach((publication) => {
                props.localPart?.unpublishTrack(publication.track);
                publication.track.detach();
                publication.track.stop();
            });
            audioTrack.detach();

            Video.createLocalAudioTrack({ deviceId: { exact: selectedMicDevice.deviceId } }).then((localAudioTrack) => {
                props.localPart?.publishTrack(localAudioTrack);
                localAudioTrack.attach(audioRef.current ?? undefined);
                setAudioTracks([localAudioTrack]);
            });
        }
    }, [selectedMicDevice]);

    // Handle attaching/detaching user video tracks
    useEffect(() => {
        const videoTrack = videoTracks[0];
        if (videoTrack) {
            videoTrack.attach(videoRef.current ?? undefined);
            return () => {
                videoTrack.detach();
            };
        }
        return undefined;
    }, [videoTracks]);

    useEffect(() => {
        const videoTrack = videoTracks[0];
        if (videoTrack && props.localPart && selectedCameraDevice) {
            props.localPart?.videoTracks.forEach((publication) => {
                props.localPart?.unpublishTrack(publication.track);
                publication.track.detach();
                publication.track.stop();
            });
            videoTrack.detach();

            Video.createLocalVideoTrack({ deviceId: { exact: selectedCameraDevice.deviceId } }).then(
                (localVideoTrack) => {
                    props.localPart?.publishTrack(localVideoTrack);
                    localVideoTrack.attach(videoRef.current ?? undefined);
                    setVideoTracks([localVideoTrack]);
                },
            );
        }
    }, [selectedCameraDevice]);

    // Handle attaching/detaching user screen share video track
    useEffect(() => {
        const screenTrack = screenTracks[0];
        // Note: screen track attach/detach is handled at the RoomVideoChat layer,
        //  not at the participant layer.
        if (screenTrack) {
            props.handleShareScreen(true, screenTrack);
            return () => {
                handleStopScreenShare(screenTrack);
            };
        }
        return undefined;
    }, [screenTracks]);

    const onScreenShareSourceSelect = useCallback((source: Electron.DesktopCapturerSource) => {
        setSelectedScreenShareSource(source);
    }, []);

    return props.participant.state !== "disconnected" ? (
        <RoomParticipantView className={`room-participant`}>
            <VideoWrapper enable={camOn ?? false}>
                {!camOn && (
                    <UserPlaceholderIconWrapper>
                        <UserPlaceholderIcon src={USER_VIDEO_PLACEHOLDER_EMOJI} referrerPolicy="no-referrer" />
                    </UserPlaceholderIconWrapper>
                )}

                <ParticipantVideo ref={videoRef} autoPlay={true} enable={camOn ?? false} />

                <ParticipantInfo camEnabled={camOn ?? false}>
                    <ParticipantInfoBackground />

                    <ParticipantName>{props.participantDisplayName}</ParticipantName>

                    {(!micOn || (props.volume === 0 && !props.isLocalClient)) && <MutedIcon />}
                </ParticipantInfo>
            </VideoWrapper>

            {/* So we don't render audio for the local participant */}
            {!props.isLocalClient && (
                <audio ref={audioRef} autoPlay={micOn} muted={!micOn || (props.volume === 0 && !props.isLocalClient)} />
            )}

            {screenShareSources && isMyScreenShared && (
                <RoomScreenShareSelectorModal
                    isOpen={true}
                    onClose={() => {
                        setScreenShareSources(undefined);
                    }}
                    screenShareSources={screenShareSources}
                    onSelect={onScreenShareSourceSelect}
                />
            )}
        </RoomParticipantView>
    ) : null;
}

const trackpubsToTracks = (tracks: RoomTypes.AudioPubType[] | RoomTypes.VideoPubType[]) => {
    return tracks.map((publication: RoomTypes.PublicationType) => publication.track).filter((track) => track != null);
};

const getUserMicTrack = (tracks: Video.AudioTrackPublication[]): Video.AudioTrackPublication | null => {
    return (
        tracks.filter(
            (t: Video.AudioTrackPublication) => t.trackName === RoomConstants.VideoChatTrackNames.USER_MIC,
        )[0] ?? null
    );
};

const filterWebcamTrack = (t: RoomTypes.VideoPubType) => {
    return t.trackName === RoomConstants.VideoChatTrackNames.USER_WEBCAM;
};

const getUserWebcamTrack = (tracks: Video.VideoTrackPublication[]): Video.VideoTrackPublication | null => {
    return tracks.filter(filterWebcamTrack)[0] ?? null;
};

const filterScreenShareTrack = (t: RoomTypes.VideoPubType) => {
    return t.trackName === RoomConstants.VideoChatTrackNames.USER_SCREEN;
};

const getUserScreenTrack = (tracks: Video.VideoTrackPublication[]): Video.VideoTrackPublication | null => {
    return tracks.filter(filterScreenShareTrack)[0] ?? null;
};
